// Cálculo client-side de "linhas equivalentes manuscritas" — espelho do
// helper do backend (apps/api/src/lib/essay-meta.ts) pra contadores ao vivo.
//
// Convenção: cada banca tem um padrão de linhas máximas e chars/linha base
// (assumindo letra média). O aluno declara o tamanho da letra (pequena/
// média/grande) e isso vira um multiplicador no chars/linha.

import type { EssayMeta } from "./api";

const PRESET_TABLE: Record<
  Exclude<NonNullable<EssayMeta["linePreset"]>, "custom" | "free">,
  { maxLines: number; charsPerLineBase: number; label: string }
> = {
  enem: { maxLines: 30, charsPerLineBase: 70, label: "ENEM" },
  fuvest: { maxLines: 30, charsPerLineBase: 70, label: "FUVEST" },
  unicamp: { maxLines: 32, charsPerLineBase: 70, label: "UNICAMP" },
  unesp: { maxLines: 30, charsPerLineBase: 75, label: "UNESP" },
  ita: { maxLines: 30, charsPerLineBase: 70, label: "ITA" }
};

const HANDWRITING_MULT: Record<
  Exclude<NonNullable<EssayMeta["handwritingSize"]>, "calibrated">,
  number
> = {
  tiny: 1.3,
  small: 1.15,
  medium: 1.0,
  large: 0.85,
  huge: 0.7
};

export interface LineEquivalence {
  preset: NonNullable<EssayMeta["linePreset"]>;
  maxLines: number | null;
  charsPerLineBase: number;
  effectiveCharsPerLine: number;
  handwritingSize: NonNullable<EssayMeta["handwritingSize"]>;
  realized: number;
}

export function computeEquivalentLines(
  content: string,
  meta: EssayMeta | null | undefined
): LineEquivalence {
  const preset = meta?.linePreset ?? "enem";
  const handwritingSize = meta?.handwritingSize ?? "medium";
  let maxLines: number | null = null;
  let charsPerLineBase = 70;

  if (preset === "custom") {
    maxLines = meta?.maxLines ?? null;
    charsPerLineBase = meta?.charsPerLineBase ?? 70;
  } else if (preset === "free") {
    maxLines = null;
    charsPerLineBase = meta?.charsPerLineBase ?? 70;
  } else {
    const p = PRESET_TABLE[preset];
    maxLines = p.maxLines;
    charsPerLineBase = p.charsPerLineBase;
  }

  let effectiveCharsPerLine: number;
  if (handwritingSize === "calibrated" && meta?.calibratedCharsPerLine) {
    effectiveCharsPerLine = Math.max(10, meta.calibratedCharsPerLine);
  } else {
    const mult =
      HANDWRITING_MULT[
        handwritingSize as Exclude<NonNullable<EssayMeta["handwritingSize"]>, "calibrated">
      ] ?? 1.0;
    effectiveCharsPerLine = Math.max(10, Math.round(charsPerLineBase * mult));
  }

  const text = (content ?? "").trim();
  let realized = 0;
  if (text) {
    for (const p of text.split(/\n/)) {
      const trimmed = p.replace(/^[\t ]+/, "");
      if (!trimmed) continue;
      realized += Math.max(1, Math.ceil(trimmed.length / effectiveCharsPerLine));
    }
  }
  return { preset, maxLines, charsPerLineBase, effectiveCharsPerLine, handwritingSize, realized };
}

export function describePreset(preset: NonNullable<EssayMeta["linePreset"]>): string {
  if (preset === "custom") return "Customizado";
  if (preset === "free") return "Livre (sem limite)";
  return PRESET_TABLE[preset]?.label ?? preset;
}

export function describeHandwriting(size: NonNullable<EssayMeta["handwritingSize"]>): string {
  if (size === "tiny") return "miúda (cabe muito mais)";
  if (size === "small") return "pequena (cabe mais)";
  if (size === "large") return "grande (cabe menos)";
  if (size === "huge") return "muito grande (cabe bem menos)";
  if (size === "calibrated") return "calibrada por você";
  return "média";
}

export const HANDWRITING_SIZES: Array<{
  key: NonNullable<EssayMeta["handwritingSize"]>;
  label: string;
  hint: string;
}> = [
  { key: "tiny", label: "Miúda", hint: "Cabe muito mais (×1.3)" },
  { key: "small", label: "Pequena", hint: "Cabe mais (×1.15)" },
  { key: "medium", label: "Média", hint: "Padrão (×1.0)" },
  { key: "large", label: "Grande", hint: "Cabe menos (×0.85)" },
  { key: "huge", label: "Muito grande", hint: "Cabe bem menos (×0.7)" },
  { key: "calibrated", label: "Minha letra (calibrada)", hint: "Usa o valor que você mediu" }
];

// ============================================================================
// Truncamento por linhas — espelho de apps/api/src/lib/caderno-truncate.ts
// Usado pra pintar em vermelho os chars excedentes na tela de resultado.
// O servidor usa a MESMA regra antes de enviar ao corretor IA, então o
// ponto de corte casa exatamente com o que a IA não vê.
// ============================================================================

interface LineLayout {
  rawEnd: number;
}

function layoutLinesForCount(raw: string, charsPerLine: number): LineLayout[] {
  const cpl = Math.max(10, Math.min(200, charsPerLine));
  const lines: LineLayout[] = [];
  let cursor = 0;
  while (cursor <= raw.length) {
    const nextNewline = raw.indexOf("\n", cursor);
    const chunkEnd = nextNewline === -1 ? raw.length : nextNewline;
    const chunk = raw.slice(cursor, chunkEnd);
    const chunkStart = cursor;
    if (chunk.length === 0) {
      lines.push({ rawEnd: chunkStart });
      cursor = chunkEnd + 1;
      if (nextNewline === -1) break;
      continue;
    }
    const tokens: Array<{ text: string; start: number; isWord: boolean }> = [];
    const re = /([a-záàâãéèêíìóòôõúùçü]+)|([^a-záàâãéèêíìóòôõúùçü]+)/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(chunk)) !== null) {
      if (m[0].length === 0) break;
      tokens.push({ text: m[0], start: m.index, isWord: !!m[1] });
    }
    let lineLen = 0;
    let i = 0;
    while (i < tokens.length) {
      const tok = tokens[i];
      if (lineLen === 0 && !tok.isWord && /^\s+$/.test(tok.text)) {
        i++;
        continue;
      }
      const remaining = cpl - lineLen;
      if (tok.text.length <= remaining) {
        lineLen += tok.text.length;
        i++;
        continue;
      }
      if (lineLen > 0) {
        lines.push({ rawEnd: chunkStart + tok.start });
        lineLen = 0;
        continue;
      }
      lines.push({ rawEnd: chunkStart + tok.start + cpl });
      tokens[i] = {
        text: tok.text.slice(cpl),
        start: tok.start + cpl,
        isWord: tok.isWord
      };
    }
    if (lineLen > 0 || nextNewline !== -1) {
      lines.push({ rawEnd: chunkEnd });
    }
    cursor = chunkEnd + 1;
    if (nextNewline === -1) break;
  }
  if (lines.length === 0) lines.push({ rawEnd: 0 });
  return lines;
}

/** Ponto de corte (índice no `raw`) tal que `raw.slice(0, cutoff)` cabe nas
 *  `maxLines` primeiras linhas visuais. Retorna `raw.length` se cabe inteiro. */
export function essayCutoffIndex(
  raw: string,
  meta: EssayMeta | null | undefined,
  charsPerLineOverride?: number
): { cutoff: number; droppedLines: number; totalLines: number } {
  if (!raw) return { cutoff: 0, droppedLines: 0, totalLines: 0 };
  const cpl = Math.max(
    30,
    Math.min(
      130,
      charsPerLineOverride ??
        (meta as Record<string, unknown> | null | undefined)?.cadernoCharsPerLine as number ??
        meta?.charsPerLineBase ??
        80
    )
  );
  const maxLines = meta?.maxLines ?? 30;
  const lines = layoutLinesForCount(raw, cpl);
  if (lines.length <= maxLines) {
    return { cutoff: raw.length, droppedLines: 0, totalLines: lines.length };
  }
  return {
    cutoff: lines[maxLines - 1].rawEnd,
    droppedLines: lines.length - maxLines,
    totalLines: lines.length
  };
}

export const LINE_PRESETS: Array<{
  key: NonNullable<EssayMeta["linePreset"]>;
  label: string;
  desc: string;
}> = [
  { key: "enem", label: "ENEM", desc: "30 linhas · ~70 chars/linha" },
  { key: "fuvest", label: "FUVEST", desc: "30 linhas · ~70 chars/linha" },
  { key: "unicamp", label: "UNICAMP", desc: "32 linhas · ~70 chars/linha" },
  { key: "unesp", label: "UNESP", desc: "30 linhas · ~75 chars/linha" },
  { key: "ita", label: "ITA", desc: "30 linhas · ~70 chars/linha" },
  { key: "custom", label: "Customizado", desc: "Você define linhas e chars/linha" },
  { key: "free", label: "Livre", desc: "Sem limite de linhas" }
];
